import { motion } from "framer-motion";
import { Disc, Heart, History, PlayCircle } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";

interface ActivityCardsProps {
  likedSongs: number;
  recentlyPlayed: number;
  topArtist: string;
  topSong: string;
  topSongUrl: string; // New prop for top song URL
}

export const ActivityCards: React.FC<ActivityCardsProps> = ({
  likedSongs,
  recentlyPlayed,
  topArtist,
  topSong,
  topSongUrl, // New prop destructured
}) => {
  const cards = [
    {
      icon: Heart,
      title: "Liked Songs",
      value: `${likedSongs} tracks`,
      color: "from-rose-500 to-pink-500",
      link: "/liked",
    },
    {
      icon: History,
      title: "Recently Played",
      value: `${recentlyPlayed} tracks today`,
      color: "from-blue-500 to-indigo-500",
      link: "/recent",
    },
    {
      icon: Disc,
      title: "Top Artist",
      value: topArtist || "Start listening!",
      color: "from-purple-500 to-violet-500",
      link: "/statistics",
    },
    {
      icon: PlayCircle,
      title: "Top Track",
      value: topSong || "Start listening!",
      color: "from-yellow-500 to-emerald-500",
      link: topSongUrl || "/statistics", // Use topSongUrl if available, otherwise fallback to /statistics
    },
  ];

  return (
    <div className="hidden md:grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card, index) => (
        <Link key={card.title} to={card.link}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="relative group h-full"
          >
            <div
              className={`absolute inset-0 bg-gradient-to-br ${card.color} opacity-20 group-hover:opacity-30 blur-xl transition-opacity rounded-lg`}
            />

            <div className="h-full relative bg-zinc-900/50 backdrop-blur-xl border border-white/5 p-6 rounded-lg md:overflow-hidden group-hover:border-white/10 transition-all duration-300 group-hover:shadow-lg group-hover:-translate-y-1">
              <div
                className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${card.color} opacity-20 blur-2xl transform translate-x-16 -translate-y-16 group-hover:translate-x-8 group-hover:-translate-y-8 transition-transform duration-500`}
              />

              <div className="flex items-center justify-center gap-4 h-full">
                <div
                  className={`absolute md:relative -left-3 md:left-[unset] -top-3 md:top-[unset] scale-75 md:scale-100  p-3 rounded-xl bg-gradient-to-br ${card.color} group-hover:scale-110 transition-transform duration-300`}
                >
                  <card.icon className="w-6 h-6 text-white" />
                </div>
                <div className="text-center md:text-left">
                  <h3 className="text-lg font-semibold mb-1">{card.title}</h3>
                  <p className="text-zinc-400">{card.value}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </Link>
      ))}
    </div>
  );
};
